import { useEffect } from "react";

interface SEOMeta {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogUrl?: string;
  ogType?: string;
  ogLocale?: string;
  twitterCard?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  jsonLd?: object[];
}

const updateMetaTag = (selector: string, attr: string, value: string) => {
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    const nameOrProperty = selector.includes('property="') ? "property" : "name";
    const propValue = selector.match(new RegExp(`${nameOrProperty}="([^"]*)"`))?.[1] || "";
    if (nameOrProperty === "property") {
      el.setAttribute("property", propValue);
    } else {
      el.setAttribute("name", propValue);
    }
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
};

const removeMetaTag = (selector: string) => {
  const el = document.querySelector(selector);
  if (el) {
    el.remove();
  }
};

const updateCanonical = (url: string) => {
  let el = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", url);
};

const updateJsonLd = (scripts: object[]) => {
  document.querySelectorAll('script[type="application/ld+json"]').forEach((s) => s.remove());
  scripts.forEach((data) => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.textContent = JSON.stringify(data);
    document.head.appendChild(script);
  });
};

export default function SEO({ meta }: { meta: SEOMeta }) {
  useEffect(() => {
    if (meta.title) {
      document.title = meta.title;
    }
    if (meta.description) {
      updateMetaTag('meta[name="description"]', "content", meta.description);
    }
    if (meta.keywords) {
      updateMetaTag('meta[name="keywords"]', "content", meta.keywords);
    }
    if (meta.canonical) {
      updateCanonical(meta.canonical);
    }
    if (meta.ogTitle) {
      updateMetaTag('meta[property="og:title"]', "content", meta.ogTitle);
    }
    if (meta.ogDescription) {
      updateMetaTag('meta[property="og:description"]', "content", meta.ogDescription);
    }
    if (meta.ogUrl) {
      updateMetaTag('meta[property="og:url"]', "content", meta.ogUrl);
    }
    if (meta.ogType) {
      updateMetaTag('meta[property="og:type"]', "content", meta.ogType);
    }
    if (meta.ogLocale) {
      updateMetaTag('meta[property="og:locale"]', "content", meta.ogLocale);
    }
    if (meta.twitterCard) {
      updateMetaTag('meta[name="twitter:card"]', "content", meta.twitterCard);
    }
    if (meta.twitterTitle) {
      updateMetaTag('meta[name="twitter:title"]', "content", meta.twitterTitle);
    }
    if (meta.twitterDescription) {
      updateMetaTag('meta[name="twitter:description"]', "content", meta.twitterDescription);
    }
    if (meta.jsonLd) {
      updateJsonLd(meta.jsonLd);
    }

    return () => {
      if (meta.jsonLd) {
        document.querySelectorAll('script[type="application/ld+json"]').forEach((s) => s.remove());
      }
    };
  }, [meta]);

  return null;
}